import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import { Card, OrganisationSelect } from '@percent/hosted-validation-form/ui';
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData';
import { useHostedValidationsFormAnalytics } from '@percent/hosted-validation-form/hooks/useHostedValidationsFormAnalytics/useHostedValidationsFormAnalytics';
import { ErrorCard } from '@percent/hosted-validation-form/ui/ErrorCard/ErrorCard';
import { ErrorVariant } from '@percent/domain/verifications';
import { useProgress } from '@percent/hosted-validation-form/hooks';

import { HostedFormStep, Organisation } from '../../HostedForm.types';
import { SelectOrganisationProps } from './SelectOrganisation.types';

export const SelectOrganisation = ({ data, setSearchQuery, isError, country, isLoading }: SelectOrganisationProps) => {
  const [organisationQuery, setOrganisationQuery] = useState('');
  const { setOrganisation, handleNext, handleBack } = useValidationStepData();
  const { setValue } = useFormContext();
  const { t } = useTranslation();
  const { track } = useHostedValidationsFormAnalytics();
  const { progress } = useProgress();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    [],
  );

  const handleSelectOrganisation = (organisation?: Organisation) => {
    track('Organisation Result Selected', {
      searchTerm: organisationQuery,
      searchResultsCount: Number(data?.data.totalResults) || 0,
      organisationId: organisation?.id,
      country,
    });
    setOrganisation(organisation);
    setValue('organisationId', organisation?.id);

    handleNext(HostedFormStep.ORGANISATION_RESULT);
  };

  useEffect(() => {
    if (isError) {
      track('Viewed SelectOrganisation Error', {
        errorLabel: ErrorVariant.INTERNAL,
        country,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  if (isError) {
    return (
      <ErrorCard
        title={t('hostedValidation.errorMessage.somethingWentWrong')}
        subtitle={t('hostedValidation.errorMessage.reloadPage')}
        variant={ErrorVariant.INTERNAL}
      />
    );
  }

  const handleOrganisationQueryChange = (value: string) => {
    setOrganisationQuery(value);
    handleDebouncedSearch(value);
    setOrganisation(undefined);
  };

  const handleAddNonProfit = () => {
    track('Element Clicked', {
      action: 'link',
      label: 'I can’t find my nonprofit',
      lastSearchQuery: organisationQuery,
      searchResultsCount: data?.data.totalResults,
      country,
    });

    handleNext(HostedFormStep.REGISTER_ORGANISATION);
  };

  return (
    <Card onBack={handleBack} title={t('hostedValidation.title.fullLegalName')} progress={progress}>
      <OrganisationSelect
        data={data?.data?.data}
        isLoading={isLoading}
        name="organisation"
        setOrganisationValue={handleSelectOrganisation}
        setQueryValue={handleOrganisationQueryChange}
        countryCode={country}
        query={organisationQuery}
        placeholder={t('hostedValidation.placeholder.nonprofitNameOrRegId')}
        onAddNonProfit={handleAddNonProfit}
      />
    </Card>
  );
};
